import AsyncSelect, { Option } from "@/components/AsyncSelect";
import { useTranslation } from "react-i18next";
import { ComponentProps, forwardRef, useCallback, useMemo } from "react";
import { ControllerRenderProps } from "react-hook-form";
import { EntityListLink } from "@/components/EntityListLink";
import { Module } from "@/constants";
import { fetchUserList } from "@/services/users";
import { SYSTEM_ROLE_CODE } from "@/schemas/user";
import { FilterOperator, FilterType, mergeFilters } from "@/lib/filters";
import { ReadUser } from "@/types/user";
import appConfiguration from "@/components/cfg/AppCfg";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";

interface ProjectPrimaryManagerSelectProps extends ControllerRenderProps {
  className?: string;
  defaultItem?: Partial<Option>;
}

const ALLOWED_PRIMARY_MANAGER_ROLES = [
  SYSTEM_ROLE_CODE.KEY_ACCOUNT_MANAGER,
  SYSTEM_ROLE_CODE.LEASING_OUTSOURCING,
  SYSTEM_ROLE_CODE.CUSTOMER_SERVICE_DIRECTOR,
  SYSTEM_ROLE_CODE.REGIONAL_DIRECTOR,
  SYSTEM_ROLE_CODE.CKM_LEAD,
  SYSTEM_ROLE_CODE.OPERATIONAL_DIRECTOR,
].join(` ${FilterOperator.OR} `);

export default forwardRef<HTMLButtonElement, ProjectPrimaryManagerSelectProps>(
  function ProjectPrimaryManagerSelect({ value, defaultItem, ...props }, ref) {
    const { t } = useTranslation();
    const name = "primary-managers";
    const options = useCreateDataTableOptions(name);
    const queryKey = useDynamicQueryParams<ReadUser>(
      [Module.Users, name],
      options,
      {
        system_role_code: {
          value: ALLOWED_PRIMARY_MANAGER_ROLES,
          filter: FilterType.EqualsString,
        },
      },
    );

    const onFilter: ComponentProps<typeof AsyncSelect>["onFilter"] =
      useCallback(
        (filterValue: string) => {
          options.setFilterValue("full_name", {
            value: filterValue,
            filter: FilterType.IncludesString,
          });
        },
        [options],
      );

    const filters = useMemo(
      () =>
        mergeFilters(options.filters, [
          {
            id: "system_role_code",
            value: {
              value: ALLOWED_PRIMARY_MANAGER_ROLES,
              filter: FilterType.EqualsString,
            },
          },
        ]),
      [options.filters],
    );

    const renderUserName = (item: ReadUser & Option) => {
      const active = item.is_active ?? defaultItem?.is_active;
      const name = item.full_name ?? item.name;
      return (
        <span className="flex flex-row flex-wrap items-center gap-1">
          <span>{name}</span>
          {active !== undefined && (
            <span className="text-gray-400 text-xs">
              (
              {active
                ? t("projectPrimaryManagerSelect.active")
                : t("projectPrimaryManagerSelect.inactive")}
              )
            </span>
          )}
        </span>
      );
    };

    return (
      <span className="flex items-center gap-1">
        <AsyncSelect
          queryKey={queryKey}
          queryFn={() =>
            fetchUserList({
              filters,
              limit: appConfiguration.selectComponentsPaginationLimit,
            })
          }
          placeholder={t("projectPrimaryManagerSelect.placeholder")}
          loadingLabel={t("projectPrimaryManagerSelect.loadingUsers")}
          renderName={renderUserName}
          {...props}
          value={value}
          defaultItem={defaultItem}
          ref={ref}
          onFilter={onFilter}
          enableFilter
        />
        <EntityListLink
          path={Module.Users}
          label={t("projectPrimaryManagerSelect.link")}
        />
      </span>
    );
  },
);
